import React from "react";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Stack,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import CloseButton from "../../../components/commonComponents/Buttons/CloseButton";
import ActionButton from "../../../components/commonComponents/Buttons/ActionButton";
import TextInputField from "../../../components/commonComponents/TextInputs/TextInputField";
import NumericInputField from "../../../components/commonComponents/TextInputs/NumericInputField";
import { Close, Info, Save } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { deleteItems, updateStateData } from "../../../Store/actions";
import {
  checkDeviceStatus,
  fetchCountryId,
  fetchStoreLocations,
  fetchTimezones,
  manageStoresPaginationCookie,
  reset_infrastructure_config_page_data,
  saveStoreLocData,
} from "../../../api_calls/infrastructure";
import { checkUserDetailsFromCookie } from "../../../api_calls/userManagement";
import StoreOwnerField, { fetchStoreOwnerOptions } from "./StoreOwnerField";
import {
  HtmlTooltip,
  StyledBlackInformationTypography,
  StyledDarkRedInformationTypography,
} from "../../../components/commonComponents/StyledComponents/styled";
import SingleSelect from "../../../components/commonComponents/Selectors/SingleSelect";
import { deleteCookie, setBatchLoadingStatus } from "../../../api_calls/requestConfig";

const EditStoreInfo = ({ open, size, usedFor, closeModal, sectionToEdit }) => {
  const userStateDetails = useSelector((state) => state.userSelections);
  const loggedUserDetails = checkUserDetailsFromCookie();
  const navigate = useNavigate();
  const [fieldError, setFieldError] = React.useState({});

  const dispatchAction = useDispatch();

  const handleFieldChange = (storeLocData) => {
    const selectedStoreData =
      typeof userStateDetails.new_store_loc_data !== "undefined" ? userStateDetails.new_store_loc_data : {};
    dispatchAction(
      updateStateData({
        new_store_loc_data: { ...selectedStoreData, ...storeLocData },
      })
    );
  };

  const automaticallySetCompany = () => {
    let data = typeof userStateDetails.store_level_retailers_list !== "undefined" ? userStateDetails.retailers_list[0] : [];
    let tree_path = [];
    tree_path.push(data.tree_path);
    handleFieldChange({
      share_list: tree_path,
      share_list_selection: [data],
    });
  };

  const loadRequiredInfo = async () => {
    setBatchLoadingStatus(true)
    return await Promise.all([
      // Branch: share-list. Cyreen and tenant: hierarchy. Other types: no owner request.
      fetchStoreOwnerOptions(),
      fetchTimezones(),
    ]).then(()=>setBatchLoadingStatus(false))
  }

  React.useEffect(() => {
    loadRequiredInfo();
    if (usedFor === "update") {
      handleFieldChange({
        id: userStateDetails?.store_overview?.id,
        store_name: userStateDetails?.store_overview?.name,
        store_country: userStateDetails?.store_overview?.country?.name,
        store_country_id: userStateDetails?.store_overview?.country?.id,
        store_region: userStateDetails?.store_overview?.region?.name,
        store_region_obj: userStateDetails?.store_overview?.region
          ? { label: userStateDetails.store_overview.region.name, id: userStateDetails.store_overview.region.id }
          : {},
        store_region_id: userStateDetails?.store_overview?.region?.id,
        store_street: userStateDetails?.store_overview?.address,
        store_zip: userStateDetails?.store_overview?.zipCode,
        store_city: userStateDetails?.store_overview?.city,
        store_trolleys: userStateDetails?.store_overview?.installed_trolleys,
        store_baskets: userStateDetails?.store_overview?.installed_baskets,
        store_size: userStateDetails?.store_overview?.store_size,
        store_status: checkDeviceStatus() === true ? "Active" : "Inactive",
        automated_checkout: userStateDetails?.store_overview?.automated === true ? "Automated" : "Manual/No",
        retailer_store_id: userStateDetails?.store_overview?.number_store_retailer,
        longitude: userStateDetails?.store_overview?.longitude,
        latitude: userStateDetails?.store_overview?.latitude,
        data_process_status: userStateDetails?.store_overview?.store_status === 1 ? "Active" : "Inactive",
        share_list: userStateDetails?.store_overview?.company?.id,
        share_list_selection: {
          label: userStateDetails?.store_overview?.company?.name,
          id: userStateDetails?.store_overview?.company?.id,
          tree_path: userStateDetails?.store_overview?.company?.tree_path,
        },
        selected_timezone: typeof userStateDetails.store_overview.timezone !== "undefined" && userStateDetails.store_overview.timezone !== null ? "(UTC " + userStateDetails?.store_overview?.timezone?.utc_offset + ") " + userStateDetails.store_overview.timezone.name : "",
        selected_timezone_id: typeof userStateDetails.store_overview.timezone !== "undefined" && userStateDetails.store_overview.timezone !== null ? userStateDetails?.store_overview?.timezone.id_timezone : "",
      });
    }
  }, []);

  return (
    <Dialog
      open={open}
      onClose={(e) => {
        e.preventDefault();
      }}
      fullWidth={true}
      maxWidth={size}
    >
      <DialogTitle>{usedFor === "update" ? "Update Store Information" : "Add Store"}</DialogTitle>
      <DialogContent>
        {["Both", "StoreDetail"].includes(sectionToEdit) && (
          <>
            {usedFor === "Add" && (
              <>
                <Box height={20} />
                <Typography variant="h6">Store Information</Typography>
              </>
            )}
            <Box height={20} />
            <Stack direction={"row"} spacing={4}>
              <TextInputField
                fieldRequired={true}
                elemId="name"
                label="Name"
                fieldName="name"
                showlabel={true}
                defaultVal={
                  typeof userStateDetails.new_store_loc_data !== "undefined" &&
                    typeof userStateDetails.new_store_loc_data.store_name !== "undefined"
                    ? userStateDetails.new_store_loc_data.store_name
                    : ""
                }
                inputType={"text"}
                onChangeCall={(e) => {
                  e.preventDefault();
                  handleFieldChange({
                    store_name: e.target.value,
                  });
                }}
                fieldError={
                  typeof fieldError.store_name !== "undefined" && fieldError.store_name !== "" ? fieldError.store_name : ""
                }
              />
              <SingleSelect
                enableFullWidth={true}
                label="Country*"
                elemId="editStoreCountry"
                readOnlyFlag={true}
                disableSearch={true}
                sortOptions={false}
                options={(userStateDetails.countries_list || []).map((country) => ({
                  label: country.name,
                  id: country.id,
                }))}
                defaultVal={
                  typeof loggedUserDetails.success !== "undefined" &&
                  loggedUserDetails.success === true &&
                  loggedUserDetails.user_detail.current_workspace.country.name !== ""
                    ? {
                        label: loggedUserDetails.user_detail.current_workspace.country.name,
                        id: fetchCountryId(loggedUserDetails.user_detail.current_workspace.country.name),
                      }
                    : ""
                }
                selectionCheck={(option, value) => option.id === value.id}
                onChangeCall={(selectedOption) => {
                  handleFieldChange({
                    store_country: selectedOption.label,
                    store_country_id: selectedOption.id,
                  });
                }}
                fieldError={
                  typeof fieldError.store_country !== "undefined" && fieldError.store_country !== ""
                    ? fieldError.store_country
                    : ""
                }
              />
            </Stack>
            <Box height={40} />
            <Stack direction={"row"} spacing={4}>
              <SingleSelect
                enableFullWidth={true}
                label="Region*"
                elemId={"regionSelectionForInfra"}
                options={typeof userStateDetails.regions_list !== "undefined" ? userStateDetails.regions_list : []}
                defaultVal={
                  typeof userStateDetails.new_store_loc_data !== "undefined" &&
                    typeof userStateDetails.new_store_loc_data.store_region_obj !== "undefined"
                    ? userStateDetails.new_store_loc_data.store_region_obj
                    : {}
                }
                selectionCheck={(option, value) => option.id === value.id}
                onChangeCall={(selectedOption) => {
                  handleFieldChange({
                    store_region: selectedOption.label,
                    store_region_id: selectedOption.id,
                    store_region_obj: selectedOption,
                    new_region: false,
                  });
                }}
                fieldError={
                  typeof fieldError.store_region !== "undefined" && fieldError.store_region !== ""
                    ? fieldError.store_region
                    : ""
                }
              />

              <TextInputField
                fieldRequired={true}
                elemId="street"
                label="Street & No."
                fieldName="street"
                defaultVal={
                  typeof userStateDetails.new_store_loc_data !== "undefined" &&
                    typeof userStateDetails.new_store_loc_data.store_street !== "undefined"
                    ? userStateDetails.new_store_loc_data.store_street
                    : ""
                }
                showlabel={true}
                inputType={"text"}
                onChangeCall={(e) => {
                  e.preventDefault();
                  handleFieldChange({
                    store_street: e.target.value,
                  });
                }}
                fieldError={
                  typeof fieldError.store_street !== "undefined" && fieldError.store_street !== ""
                    ? fieldError.store_street
                    : ""
                }
              />
            </Stack>
            <Box height={40} />
            <Stack direction={"row"} spacing={4}>
              <TextInputField
                fieldRequired={true}
                elemId="zip_code"
                label="Zip Code"
                fieldName="zip_code"
                defaultVal={
                  typeof userStateDetails.new_store_loc_data !== "undefined" &&
                    typeof userStateDetails.new_store_loc_data.store_zip !== "undefined"
                    ? userStateDetails.new_store_loc_data.store_zip
                    : ""
                }
                showlabel={true}
                inputType={"text"}
                onChangeCall={(e) => {
                  e.preventDefault();
                  handleFieldChange({
                    store_zip: e.target.value,
                  });
                }}
                fieldError={
                  typeof fieldError.store_zip !== "undefined" && fieldError.store_zip !== "" ? fieldError.store_zip : ""
                }
              />
              <TextInputField
                fieldRequired={true}
                elemId="city"
                label="City"
                fieldName="city"
                defaultVal={
                  typeof userStateDetails.new_store_loc_data !== "undefined" &&
                    typeof userStateDetails.new_store_loc_data.store_city !== "undefined"
                    ? userStateDetails.new_store_loc_data.store_city
                    : ""
                }
                showlabel={true}
                inputType={"text"}
                onChangeCall={(e) => {
                  e.preventDefault();
                  handleFieldChange({
                    store_city: e.target.value,
                  });
                }}
                fieldError={
                  typeof fieldError.store_city !== "undefined" && fieldError.store_city !== "" ? fieldError.store_city : ""
                }
              />
            </Stack>
            <Box height={40} />
            <Stack direction={"row"} spacing={4}>
              <TextInputField
                fieldRequired={true}
                elemId="Latitude"
                label="Latitude"
                fieldName="Latitude"
                defaultVal={
                  typeof userStateDetails.new_store_loc_data !== "undefined" &&
                    typeof userStateDetails.new_store_loc_data.latitude !== "undefined"
                    ? userStateDetails.new_store_loc_data.latitude
                    : ""
                }
                showlabel={true}
                inputType={"text"}
                onChangeCall={(e) => {
                  e.preventDefault();
                  handleFieldChange({
                    latitude: e.target.value,
                  });
                }}
                fieldError={
                  typeof fieldError.latitude !== "undefined" && fieldError.latitude !== "" ? fieldError.latitude : ""
                }
                iconProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <HtmlTooltip
                        title={
                          <Box width={250}>
                            <Typography variant="body2">
                              Please use accurate information for latitude and longitude. We recommend using the information
                              displayed by Google Maps. The order of values used by Google Maps is latitude followed by
                              longitude.
                            </Typography>
                          </Box>
                        }
                      >
                        <IconButton>
                          <Info />
                        </IconButton>
                      </HtmlTooltip>
                    </InputAdornment>
                  ),
                }}
              />
              <TextInputField
                fieldRequired={true}
                elemId="Longitude"
                label="Longitude"
                fieldName="longitude"
                defaultVal={
                  typeof userStateDetails.new_store_loc_data !== "undefined" &&
                    typeof userStateDetails.new_store_loc_data.longitude !== "undefined"
                    ? userStateDetails.new_store_loc_data.longitude
                    : ""
                }
                showlabel={true}
                inputType={"text"}
                onChangeCall={(e) => {
                  e.preventDefault();
                  handleFieldChange({
                    longitude: e.target.value,
                  });
                }}
                fieldError={
                  typeof fieldError.longitude !== "undefined" && fieldError.longitude !== "" ? fieldError.longitude : ""
                }
                iconProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <HtmlTooltip
                        title={
                          <Box width={250}>
                            <Typography variant="body2">
                              Please use accurate information for latitude and longitude. We recommend using the information
                              displayed by Google Maps. The order of values used by Google Maps is latitude followed by
                              longitude.
                            </Typography>
                          </Box>
                        }
                      >
                        <IconButton>
                          <Info />
                        </IconButton>
                      </HtmlTooltip>
                    </InputAdornment>
                  ),
                }}
              />
            </Stack>
            <Box height={40} />
            <Stack direction={"row"} spacing={4}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <SingleSelect
                  label="Time Zone*"
                  elemId={"timezoneForInfra"}
                  enableFullWidth={true}
                  sortOptions={false}
                  readOnlyFlag={false}
                  options={
                    typeof userStateDetails.infra_timezones !== "undefined"
                      ? userStateDetails.infra_timezones
                      : []
                  }
                  defaultVal={
                    typeof userStateDetails.new_store_loc_data !== "undefined" &&
                      typeof userStateDetails.new_store_loc_data.selected_timezone !== "undefined"
                      ? {
                        label: userStateDetails.new_store_loc_data.selected_timezone,
                        id: userStateDetails.new_store_loc_data.selected_timezone_id,
                      }
                      : ""
                  }
                  selectionCheck={(option, value) => option?.id === value?.id}
                  onChangeCall={(selectedOption) => {
                    handleFieldChange({
                      selected_timezone: selectedOption.label,
                      selected_timezone_id: selectedOption.id,
                    });
                  }}
                  fieldError={
                    typeof fieldError.selected_timezone !== "undefined" && fieldError.selected_timezone !== ""
                      ? fieldError.selected_timezone
                      : ""
                  }
                />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                {/* Branch: flat list. Cyreen and tenant: company tree, prefilled with this store's company. */}
                <StoreOwnerField
                  elemId="ownerSelectionForInfra"
                  fieldError={
                    typeof fieldError.share_list_selection !== "undefined" && fieldError.share_list_selection !== ""
                      ? fieldError.share_list_selection
                      : ""
                  }
                />
              </Box>
            </Stack>
          </>
        )}

        {["Both", "StoreInfrastructure"].includes(sectionToEdit) && (
          <>
            <Box height={20} />
            {usedFor === "Add" && (
              <>
                <Typography variant="h6">Store Infrastructure</Typography>
                <Box height={20} />
              </>
            )}
            {usedFor === "update" && (
              <>
                <Box>
                  <Stack direction={"row"} spacing={3}>
                    <Box>
                      <Stack direction={"row"} spacing={1}>
                        <StyledDarkRedInformationTypography variant="body2">Cyreen Store ID :</StyledDarkRedInformationTypography>
                        <StyledBlackInformationTypography variant="body2" sx={{ fontWeight: "normal" }}>
                          {userStateDetails?.store_overview ? (
                            <strong>{userStateDetails?.store_overview?.number_store_cyreen}</strong>
                          ) : (
                            <strong>__</strong>
                          )}
                        </StyledBlackInformationTypography>
                      </Stack>
                    </Box>
                    <Box>
                      <Stack direction={"row"} spacing={1}>
                        <StyledDarkRedInformationTypography variant="body2">Store IP Address :</StyledDarkRedInformationTypography>
                        <StyledBlackInformationTypography variant="body2" sx={{ fontWeight: "normal" }}>
                          {userStateDetails?.db_store_ip ? (
                            <strong>
                              10.{userStateDetails?.db_store_ip?.second_octet}.{userStateDetails?.db_store_ip?.third_octet}
                              .0/24
                            </strong>
                          ) : (
                            <strong>__</strong>
                          )}
                        </StyledBlackInformationTypography>
                      </Stack>
                    </Box>
                    <Box>
                      <Stack direction={"row"} spacing={1}>
                        <StyledDarkRedInformationTypography variant="body2">Store Status :</StyledDarkRedInformationTypography>
                        <StyledBlackInformationTypography variant="body2" sx={{ fontWeight: "normal" }}>
                          <strong>
                            {typeof userStateDetails.new_store_loc_data !== "undefined" &&
                              typeof userStateDetails.new_store_loc_data.store_status !== "undefined"
                              ? userStateDetails.new_store_loc_data.store_status
                              : ""}{" "}
                          </strong>
                        </StyledBlackInformationTypography>
                        <HtmlTooltip
                          title={
                            <Box width={450}>
                              <Typography variant="body2">
                                Store Status is determined based on your Devices settings.
                              </Typography>
                              <Box height={5} />
                              <Typography variant="body2">
                                <Box>
                                  <strong>Store Active:</strong> Active Devices
                                  {" >"} 0
                                </Box>
                                <Box height={2} />
                                <Box>
                                  <strong>Store Inactive:</strong> Active Devices = 0
                                </Box>
                              </Typography>
                            </Box>
                          }
                        >
                          <Info sx={{ fontSize: "18px" }} />
                        </HtmlTooltip>
                      </Stack>
                    </Box>
                    <Box>
                      <Stack direction={"row"} spacing={1}>
                        <StyledDarkRedInformationTypography variant="body2">Data Processing :</StyledDarkRedInformationTypography>
                        <StyledBlackInformationTypography variant="body2" sx={{ fontWeight: "normal" }}>
                          <strong>{userStateDetails?.store_overview?.store_status === 1 ? "Active" : "Inactive"}</strong>
                        </StyledBlackInformationTypography>
                      </Stack>
                    </Box>
                  </Stack>
                </Box>

                <Box height={30} />
              </>
            )}

            <Stack direction={"row"} spacing={4}>
              <NumericInputField
                fieldRequired={false}
                elemId="retailer_store_id"
                label={"Retailer Store ID"}
                fieldName="retailer_store_id"
                errorStyle="text"
                defaultVal={
                  typeof userStateDetails.new_store_loc_data !== "undefined" &&
                    typeof userStateDetails.new_store_loc_data.retailer_store_id !== "undefined"
                    ? userStateDetails.new_store_loc_data.retailer_store_id
                    : ""
                }
                onChangeCall={(e) => {
                  e.preventDefault();
                  handleFieldChange({
                    retailer_store_id: e.target.value,
                  });
                }}
                fieldError={
                  typeof fieldError.retailer_store_id !== "undefined" && fieldError.retailer_store_id !== ""
                    ? fieldError.retailer_store_id
                    : ""
                }
                fieldInfo={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <HtmlTooltip
                        title={
                          <Box width={250}>
                            <Typography variant="body2">
                              <strong>Retailer Store ID:</strong> Refers to the internal store ID assigned by the retailer
                              and is included in the receipt data. The Retailer Store ID is essential for processing this
                              store’s receipt data.
                            </Typography>
                          </Box>
                        }
                      >
                        <IconButton>
                          <Info />
                        </IconButton>
                      </HtmlTooltip>
                    </InputAdornment>
                  ),
                }}
              />
              <SingleSelect
                enableFullWidth={true}
                disableSearch={true}
                label="Checkout Tracking*"
                elemId={"checkoutTracking"}
                readOnlyFlag={false}
                options={["Automated", "Manual/No"].map((trackVal) => {
                  return { label: trackVal, value: trackVal };
                })}
                defaultVal={
                  typeof userStateDetails.new_store_loc_data !== "undefined" &&
                    typeof userStateDetails.new_store_loc_data.automated_checkout !== "undefined"
                    ? {
                      label: userStateDetails.new_store_loc_data.automated_checkout,
                      value: userStateDetails.new_store_loc_data.automated_checkout,
                    }
                    : { label: "Automated", value: "Automated" }
                }
                selectionCheck={(option, value) => option.value === value.value}
                onChangeCall={(selectedOption) => {
                  handleFieldChange({
                    automated_checkout: selectedOption.value,
                  });
                }}
                fieldError={
                  typeof fieldError.automated_checkout !== "undefined" && fieldError.automated_checkout !== ""
                    ? fieldError.automated_checkout
                    : ""
                }
                fieldInfo={
                  <InputAdornment position="end" sx={{ marginRight: "15px" }}>
                    <HtmlTooltip
                      title={
                        <Box width={250}>
                          <Typography variant="body2">
                            <strong>Automated:</strong> Shoppers are automatically recorded at the checkouts using C.A.P.
                            tracking technology. <br />
                            <strong>Manual/No:</strong> Shoppers are manually recorded at the checkouts by the store's
                            personnel or are not recorded at all.
                          </Typography>
                        </Box>
                      }
                    >
                      <IconButton>
                        <Info />
                      </IconButton>
                    </HtmlTooltip>
                  </InputAdornment>
                }
              />
            </Stack>
            <Box height={40} />
            <Stack direction={"row"} spacing={4}>
              <NumericInputField
                fieldRequired={false}
                elemId="equipped_trolleys"
                label="Equipped Trolleys"
                fieldName="equipped_trolleys"
                errorStyle="text"
                defaultVal={
                  typeof userStateDetails.new_store_loc_data !== "undefined" &&
                    typeof userStateDetails.new_store_loc_data.store_trolleys !== "undefined"
                    ? userStateDetails.new_store_loc_data.store_trolleys
                    : ""
                }
                onChangeCall={(e) => {
                  e.preventDefault();
                  handleFieldChange({
                    store_trolleys: e.target.value,
                  });
                }}
                fieldError={
                  typeof fieldError.store_trolleys !== "undefined" && fieldError.store_trolleys !== ""
                    ? fieldError.store_trolleys
                    : ""
                }
              />
              <NumericInputField
                fieldRequired={false}
                elemId="equipped_baskets"
                label="Equipped Baskets"
                errorStyle="text"
                fieldName="equipped_baskets"
                defaultVal={
                  typeof userStateDetails.new_store_loc_data !== "undefined" &&
                    typeof userStateDetails.new_store_loc_data.store_baskets !== "undefined"
                    ? userStateDetails.new_store_loc_data.store_baskets
                    : ""
                }
                onChangeCall={(e) => {
                  e.preventDefault();
                  handleFieldChange({
                    store_baskets: e.target.value,
                  });
                }}
                fieldError={
                  typeof fieldError.store_baskets !== "undefined" && fieldError.store_baskets !== ""
                    ? fieldError.store_baskets
                    : ""
                }
              />

              <TextInputField
                fieldRequired={false}
                elemId="store_size"
                label="Store Size (in m²)"
                fieldName="store_size"
                showlabel={true}
                defaultVal={
                  typeof userStateDetails.new_store_loc_data !== "undefined" &&
                    typeof userStateDetails.new_store_loc_data.store_size !== "undefined"
                    ? userStateDetails.new_store_loc_data.store_size
                    : ""
                }
                inputType={"text"}
                onChangeCall={(e) => {
                  e.preventDefault();
                  handleFieldChange({
                    store_size: e.target.value,
                  });
                }}
                fieldError={
                  typeof fieldError.store_size !== "undefined" && fieldError.store_size !== "" ? fieldError.store_size : ""
                }
              />
            </Stack>
          </>
        )}
      </DialogContent>
      <Box height={20} />
      <DialogActions>
        <Stack direction={"row"} spacing={2}>
          <CloseButton
            onClick={(e) => {
              e.preventDefault();
              closeModal();
            }}
            label="Cancel"
            icon={<Close />}
          />
          <ActionButton
            onClick={(e) => {
              e.preventDefault();
              setFieldError({});
              saveStoreLocData(usedFor).then((info) => {
                if (info.success === false) {
                  if (typeof info.errorMessages !== "undefined") {
                    setFieldError(info.errorMessages);
                  }
                } else {
                  if (usedFor === "update") {
                    reset_infrastructure_config_page_data(userStateDetails.new_store_loc_data.id);
                  } else {
                    // reset filters
                    // dispatchAction(deleteItems(["store_list_filters", "store_list_filter_applied"]));
                    // deleteCookie("store_list_filters", true);
                    // manageStoresPaginationCookie({ scroll_pos: 0 });
                    fetchStoreLocations(0, userStateDetails.stores_pagination.pageSize); // refresh the locations list
                    navigate("/infrastructure/configure-location/" + info.store_id);
                  }
                  closeModal();
                }
              });
            }}
            label={usedFor === "update" ? "Update" : "Save"}
            icon={<Save />}
          />
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

export default EditStoreInfo;
